import { useMemo, useState } from 'react';
import { CATEGORIES, FEED_ARTICLES, type FeedArticle, type TagKey } from '@/mocks/celebrity';

export const TAG_LABEL: Record<TagKey, string> = {
  love: '恋情公开',
  dating: '约会实锤',
  breakup: '分手复合',
  timeline: '感情线盘点',
};

const TAG_ANALYSIS: Record<TagKey, string> = {
  love: '一段被看好的公开恋情，从来不是靠一纸声明维系，而是靠一次次经得起扒的同款、同框与长文里的真诚细节累积起来的口碑。对粉丝而言，最动人的往往不是官宣那天的热搜，而是此后每一条不刻意炫耀、却藏不住爱意的日常。',
  dating: '被拍到的“实锤”之所以能在短时间内冲上热搜，靠的是多维度证据的互相印证：行程轨迹、同款物件、身边人旁证缺一不可。越是被遮遮掩掩地离场，越坐实了粉丝心里的那份期待——真正的恋爱，往往低调又坚定。',
  breakup: '明星分手之所以让人意难平，是因为在镁光灯下，感情早已不只是两个人的事。体面告别的背后，是团队反复权衡的声明措辞，也是两个成年人对过去六年的尊重。分开不一定是不爱，也可能只是两个人的时区没能同步。',
  timeline: '把散落在综艺、动态与访谈里的线索，按时间线一颗颗拼回原位，往往是看清一段感情最诚实的方式。感情线盘点做的不是八卦定论，而是把“那些当时没注意的巧合”整理成看得懂的线索，交给时间去证明答案。',
};

const ANALYSIS_POINTS = [
  '结合行程轨迹与同款物件交叉验证，确认事件可信度等级',
  '回溯当事人近期公开动态，还原事件发生前后的完整脉络',
  '持续跟进当事人与工作室后续回应，第一时间更新进展',
];

export default function CategoryFeed() {
  const [active, setActive] = useState<'all' | TagKey>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<FeedArticle | null>(null);

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: FEED_ARTICLES.length };
    FEED_ARTICLES.forEach((a) => {
      map[a.tag] = (map[a.tag] || 0) + 1;
    });
    return map;
  }, []);

  const list = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return FEED_ARTICLES.filter((a) => {
      const matchTag = active === 'all' || a.tag === active;
      const matchKw = !kw || a.title.toLowerCase().includes(kw) || a.summary.toLowerCase().includes(kw);
      return matchTag && matchKw;
    });
  }, [active, query]);

  return (
    <section id="feeds" className="bg-background-100/60">
      <div className="mx-auto max-w-[1200px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">SECTIONS</p>
            <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              栏目吃瓜 · 按口味挑选
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              想看公开撒糖还是分手复盘？点按栏目切换，或用关键词搜你想找的瓜。
            </p>
          </div>
          <label className="relative block w-full lg:max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜明星、事件或栏目…"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </label>
        </div>

        {/* category tabs */}
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActive('all')}
            className={`inline-flex cursor-pointer items-center whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
              active === 'all'
                ? 'bg-primary-500 font-medium text-background-50'
                : 'bg-background-50 text-foreground-700 hover:bg-secondary-100'
            }`}
          >
            全部吃瓜
            <span className="ml-1.5 text-xs opacity-80">{counts.all}</span>
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              className={`inline-flex cursor-pointer items-center whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === c.key
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'bg-background-50 text-foreground-700 hover:bg-secondary-100'
              }`}
            >
              {c.label}
              <span className="ml-1.5 text-xs opacity-80">{counts[c.key] || 0}</span>
            </button>
          ))}
        </div>

        {/* feed grid */}
        {list.length === 0 ? (
          <div className="mt-10 rounded-lg border border-background-200 bg-background-50 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300" />
            <p className="mt-3 text-sm text-foreground-600">没找到相关瓜，换个关键词再试试～</p>
            <button
              type="button"
              onClick={() => setQuery('')}
              className="mt-4 inline-flex cursor-pointer items-center whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm text-background-50 hover:bg-secondary-600"
            >
              清除搜索
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {list.map((a) => (
              <article
                key={a.id}
                onClick={() => setSelected(a)}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-300"
              >
                <div className="relative aspect-[3/4] w-full overflow-hidden">
                  <img
                    src={a.image}
                    alt={`${a.title} 娱乐资讯配图`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-950/70 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur">
                    {TAG_LABEL[a.tag]}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span>{a.date}</span>
                    <span className="inline-flex items-center gap-0.5">
                      <i className="ri-time-line" />
                      {a.minutes}分钟
                    </span>
                    <span className="ml-auto inline-flex items-center gap-0.5 text-primary-600">
                      <i className="ri-fire-fill" />
                      {(a.heat / 10000).toFixed(0)}万
                    </span>
                  </div>
                  <h3 className="mt-2 line-clamp-2 font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {a.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{a.summary}</p>
                  <div className="mt-auto flex items-center justify-between pt-3 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-chat-3-line" />
                      {a.comments.toLocaleString()}
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-primary-600">
                      点击细读
                      <i className="ri-arrow-right-s-line text-base transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* reading modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-background-50 shadow-xl"
          >
            <div className="relative h-52 w-full md:h-64">
              <img
                src={selected.image}
                alt={`${selected.title} 阅读页配图`}
                className="h-full w-full object-cover object-top"
              />
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="关闭阅读"
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-foreground-950/60 text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-close-line text-xl" />
              </button>
              <span className="absolute bottom-4 left-5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-foreground-950">
                {TAG_LABEL[selected.tag]} · 编辑部细读
              </span>
            </div>
            <div className="p-5 md:p-7">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-time-line" />
                  {selected.date}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-book-read-line" />
                  {selected.minutes}分钟读完
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {(selected.heat / 10000).toFixed(0)}万阅读
                </span>
                <span className="inline-flex items-center gap-1 text-secondary-700">
                  <i className="ri-pencil-line" />
                  {selected.author}
                </span>
              </div>
              <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                {selected.title}
              </h3>
              <p className="mt-4 text-[15px] leading-7 text-foreground-800">{selected.summary}</p>

              <h4 className="mt-6 flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-vip-diamond-line text-accent-600" />
                吃瓜要点 · 编辑部为你划重点
              </h4>
              <ul className="mt-3 space-y-2">
                {ANALYSIS_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />
                    {p}
                  </li>
                ))}
              </ul>

              <h4 className="mt-6 flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-quill-pen-fill text-primary-600" />
                编辑部观点
              </h4>
              <p className="mt-3 rounded-lg bg-secondary-100/70 p-4 text-sm leading-6 text-foreground-800">
                {TAG_ANALYSIS[selected.tag]}
              </p>

              <p className="mt-6 border-t border-background-200 pt-4 text-xs leading-5 text-foreground-500">
                以上内容依据公开可查物料与可信来源综合整理，具体以当事人及工作室官方回应为准。本站对事件保持客观呈现，不构成对任何个人的评价。
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}